import { FC, useState } from 'react'
import classNames from 'classnames'
import { useAdPerfBreakdown } from 'src/reactQuery/useAdPerformance'
import { AdPerfBreakdownRow, AdPerfRange } from 'src/services/adPerformanceApi'
import s from './dashboard.module.scss'
import { BulbIcon } from './icons'
import { genderLabel, money } from '../format'

type Metric = 'clicks' | 'impressions' | 'leads' | 'spend'
const METRICS: { key: Metric; label: string }[] = [
  { key: 'spend', label: 'Spend' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'leads', label: 'Leads' },
]

interface Props {
  currency: string
  range: AdPerfRange
}

// Compares each band's share of spend with its share of the chosen metric
// (leads by default when "Spend" is picked), plus cost per lead.
export const AudienceAnalysis: FC<Props> = ({ currency, range }) => {
  const [dim, setDim] = useState<'age' | 'gender'>('age')
  const [metric, setMetric] = useState<Metric>('spend')
  const { data } = useAdPerfBreakdown(range, dim)
  const rows = (data?.rows ?? [])
    .filter((r) => !['Unknown', 'unknown'].includes(r.value))
    .sort((a, b) => (a.value < b.value ? -1 : 1))
  const compareKey = metric === 'spend' ? 'leads' : metric
  const shareOf = (r: AdPerfBreakdownRow, k: Metric) =>
    (r[`share_${k}` as keyof AdPerfBreakdownRow] as number | null) ?? 0
  const label = (v: string) => (dim === 'gender' ? genderLabel(v) : v)

  // Best = lowest cost per lead among bands with a real slice of the budget.
  const withLeads = rows.filter((r) => r.leads > 0 && shareOf(r, 'spend') >= 5)
  const best = withLeads.reduce<AdPerfBreakdownRow | null>(
    (a, r) =>
      !a || (r.cost_per_lead ?? Infinity) < (a.cost_per_lead ?? Infinity)
        ? r
        : a,
    null
  )
  const worst = withLeads.reduce<AdPerfBreakdownRow | null>(
    (a, r) => (!a || (r.cost_per_lead ?? 0) > (a.cost_per_lead ?? 0) ? r : a),
    null
  )
  const maxShare = Math.max(
    1,
    ...rows.flatMap((r) => [shareOf(r, 'spend'), shareOf(r, compareKey)])
  )
  const compareLabel = METRICS.find((m) => m.key === compareKey)?.label ?? ''

  return (
    <div className={s.card}>
      <div className={s.cardHeader}>
        <h3 className={s.cardTitle}>Audience Analysis</h3>
        <div className={s.segmented}>
          {(['age', 'gender'] as const).map((d) => (
            <button
              className={classNames(s.segment, {
                [s.segmentActive]: dim === d,
              })}
              key={d}
              onClick={() => setDim(d)}
              type="button"
            >
              {d === 'age' ? 'Age' : 'Gender'}
            </button>
          ))}
        </div>
      </div>
      <div className={s.chips}>
        {METRICS.map((m) => (
          <button
            className={classNames(s.chip, { [s.chipActive]: metric === m.key })}
            key={m.key}
            onClick={() => setMetric(m.key)}
            type="button"
          >
            {m.label}
          </button>
        ))}
      </div>
      {best ? (
        <>
          <p className={s.headline}>
            {label(best.value)} is your best audience
          </p>
          <p className={s.subtle}>
            {best.share_leads}% of leads for {best.share_spend}% of spend, at{' '}
            {money(best.cost_per_lead, currency, 2)} per lead.
          </p>
        </>
      ) : (
        <p className={s.subtle}>
          No leads in this period to compare cost per lead.
        </p>
      )}
      <div className={s.bars}>
        {rows.map((r) => (
          <div className={s.barGroup} key={r.value}>
            <span className={s.barTip}>
              <small>Spend</small>
              <strong>{money(r.spend, currency)}</strong>
              <small>Leads</small>
              <strong>{r.leads}</strong>
            </span>
            <div
              className={classNames(s.bar, s.barA)}
              style={{ height: `${(shareOf(r, 'spend') / maxShare) * 100}%` }}
            />
            <div
              className={classNames(s.bar, s.barB)}
              style={{
                height: `${(shareOf(r, compareKey) / maxShare) * 100}%`,
              }}
            />
          </div>
        ))}
      </div>
      <div className={s.barLabels}>
        {rows.map((r) => (
          <div key={r.value}>
            <div className={s.barLabel}>{label(r.value)}</div>
            <div className={s.barShares}>
              {shareOf(r, 'spend')}% → {shareOf(r, compareKey)}%
            </div>
            <span
              className={classNames(s.costChip, {
                [s.costChipBest]: r === best,
                [s.costChipWorst]: r === worst && r !== best,
              })}
            >
              {money(r.cost_per_lead, currency, 2)}
            </span>
          </div>
        ))}
      </div>
      <div className={s.legend} style={{ marginTop: 16 }}>
        <span>
          <i style={{ background: '#d1cdf6' }} />
          Share of spend
        </span>
        <span>
          <i style={{ background: '#8080ff' }} />
          Share of {compareLabel.toLowerCase()}
        </span>
        <span>Chip = cost per lead</span>
      </div>
      {best && worst && worst !== best && (
        <div className={s.tip}>
          <BulbIcon />
          <span>
            <strong>
              {label(worst.value)} receives {worst.share_spend}% of the budget
              but only {worst.share_leads}% of leads.
            </strong>{' '}
            Leads from {label(best.value)} cost{' '}
            {Math.round(
              (1 - (best.cost_per_lead ?? 0) / (worst.cost_per_lead ?? 1)) * 100
            )}
            % less.
          </span>
        </div>
      )}
    </div>
  )
}
